import { Fragment } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useLocation, useParams } from 'react-router'
import { ButtonPrimary } from '../../../../../baseComponents/Button'
import { ComponentWithPermissions } from '../../../../../baseComponents/CreateRoutering/types'
import PermissionChecker from '../../../../../baseComponents/PermissionChecker'
import { TwinTableLink } from '../../../../../baseComponents/TwinTable'
import BaseScreenTableInner from '../../../../../baseComponents/TwinTable/BaseScreenTableInner'
import { TableComponentBaseScreenInnerProps } from '../../../../../baseComponents/TwinTable/BaseScreenTableInner/types'
import { CustomRenderBoolean, CustomRenderString } from '../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnTableSchema } from '../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import { ParseDataFunc } from '../../../../../baseComponents/TwinTable/types'
import TwinTrans from '../../../../../baseComponents/TwinTrans'
import { getBasePath } from '../../../../../utils/globals/link'
import { makeVariantGroupMainIdFirst, sortVariantProducts } from '../../../../../utils/globals/product'
import withLoading from '../../../../../utils/hoc/withLoading'
import useProductActiveDictionary from '../../../../../utils/hooks/product/useProductActiveDictionary'
import useEditDeleteModal from '../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import { VariantsPayload } from '../../../../../utils/reducers/products/variants'
import { ProductExtendedTyLong } from '../../../types'
import ModalCEProductVariant from './ModalCEProductVariant'
import ModalEditProductVariants from './ModalEditProductVariants'
import { productVariantsToString } from './functions'
import { ModalEditComponentProps } from '../../../../../baseComponents/ModalsLayouts/types'

interface VariantProductsProps extends ComponentWithPermissions {
    basicData: ProductExtendedTyLong
}

const VariantProducts: React.FC<VariantProductsProps> = ({ basicData, userPermissions }) => {
    const { t } = useTwinTranslation()

    const pageData = {
        title: t('variants', 'Variantes'),
        newText: t('addProductVariant', 'Añadir nueva variante'),
    }
    return (
        <BaseScreenTableInner TableComponent={TableConnect} CreateModalComponent={ModalCEProductVariant} pageData={pageData} userPermissions={userPermissions} extraWhereParams={{ 'Product.ProductId': basicData?.id, VariantGroupMainId: basicData.VariantGroupMainId }} RightHeader={RightHeaderVariantProducts}  basicData={basicData} />
    )
}

interface RightHeaderVariantProductsProps extends ModalEditComponentProps {
}

const RightHeaderVariantProducts: React.FC<RightHeaderVariantProductsProps> = ({ onSubmit, userPermissions, basicData }) => {
    const {id} = useParams()
    const { openModal, setOpenModal } = useRightHeaderVariantProducts()
    return (
        <Fragment>
            <PermissionChecker userPermissions={userPermissions || false} permission={'update'}>
                <ButtonPrimary className='mr-10' onClick={() => setOpenModal({ type: 'edit', allRowData: { id } })}><TwinTrans transKey='updateVariants'>Editar variantes</TwinTrans></ButtonPrimary>
                {openModal?.type === 'edit' ? <ModalEditProductVariants onSubmit={onSubmit} setOpened={() => setOpenModal(null)} userPermissions={userPermissions} allRowData={{ id, ...basicData }} /> : null}
            </PermissionChecker>
        </Fragment>
    )
}

const useRightHeaderVariantProducts = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()

    return { openModal, setOpenModal }
}


type TableVariantProductsProps = TableComponentBaseScreenInnerProps & TableVariantProductsLogicProps

const TableVariantProducts: React.FC<TableVariantProductsProps> = ({ setOpened, setTableInstance, extraWhereParams, variants }) => {
    const { t } = useTwinTranslation()
    const variantsParsed: any = { ...variants, orderedGroupsJson: makeVariantGroupMainIdFirst(variants?.orderedGroupsJson || {}, extraWhereParams.VariantGroupMainId) }
    const { productActiveDict } = useProductActiveDictionary()
    const location = useLocation()
    const basePath = getBasePath(location.pathname, String(extraWhereParams?.['Product.ProductId']), true)
    const parseData: ParseDataFunc = (data) => sortVariantProducts(data, variantsParsed)
    const columns: ColumnTableSchema<ProductExtendedTyLong> = [
        {
            id: 'name',
            dataKey: 'ProductLangs.name',
            label: t('name', 'Nombre'),
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('name', 'Nombre') }
            },
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={allRowData?.ProductLangs?.[0]?.name} />
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            minWidth: 110,
            searchableComponent: {
                component: 'SearchableComponentBasic',
                extraComponentData: { label: t('reference', 'Referencia') }
            }
        },
        {
            id: 'id',
            dataKey: 'id',
            label: t('id', 'ID'),
            customRender: (_parameterValue, allRowData) => <CustomRenderString value={productVariantsToString(allRowData?.ProductVariantGroup, variants?.values || {})} />
        },
        {
            id: 'active',
            dataKey: 'active',
            label: t('active', 'Activo'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={45} />,
            hideColumnByDefault: true,
            searchableComponent: {
                component: 'SearchableComponentsCustomSelectSingle',
                extraComponentData: { items: productActiveDict, label: t('active', 'Activo') }
            },
            generalSearchable: false
        },
    ]
    return (
        <TwinTableLink columns={columns} name='subProducts' getDataFrom='/api/app/product/getAllSubProducts'
            onRowClick={(_id, rowData) => setOpened({
                type: 'edit',
                allRowData: rowData
            })} setTableInstance={setTableInstance} extraWhereParams={{ 'Product.ProductId': extraWhereParams['Product.ProductId'] }} parseData={parseData} createRowDataLink={[basePath, ['$id', 'main']]} />
    )
}

interface TableVariantProductsLogicProps extends ReduxVariants { }

const VariantsDispatch = {
    setVariants: (payload: VariantsPayload) => ({ type: 'CHANGE_VARIANTS', payload }),
}

export type ReduxVariants = ConnectedProps<typeof VariantsConnect>
const mapVariantsConnector = (state: AllReduxPayloads) => ({ variants: state.variants })
const VariantsConnect = connect(mapVariantsConnector, VariantsDispatch)

const VariantsConnectLoading = withLoading(TableVariantProducts, [{ fetchUrl: '/api/app/product/productVariant/getAllVariantsComplete', propName: 'variants', setFunctionName: 'setVariants' }])

const TableConnect = VariantsConnect(VariantsConnectLoading)

export default VariantProducts;